import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  ModalFooter,
  ModalFooterProps,
  ModalHeader,
  ModalHeaderProps,
} from './components';

export interface ModalProps {
  isOpened: boolean;
  children?: React.ReactNode;
  headerProps?: Omit<ModalHeaderProps, 'onClose'>;
  footerProps?: Omit<ModalFooterProps, 'onCancel'>;
  width?: number;
  closeOnClickOutside?: boolean;
  isConfirmation?: boolean;
  className?: string;
  onAnimationEnd?: () => void;
  onClose: () => void;
}

const Modal: React.FC<ModalProps> = ({
  isOpened: isOpenedProp,
  children,
  headerProps,
  footerProps,
  width,
  className,
  closeOnClickOutside = false,
  isConfirmation = false,
  onAnimationEnd,
  onClose,
}) => {
  const [isOpened, setIsOpened] = useState(isOpenedProp);

  const attrs = {
    container: {
      className: `modal${!isOpenedProp ? ' modal--closing' : ''}${
        isConfirmation ? ' modal--confirmation' : ''
      }${className ? ` ${className}` : ''}`,
      role: 'presentation',
      onClick: () => {
        if (closeOnClickOutside) {
          onClose();
        }
      },
      onAnimationEnd: () => {
        if (!isOpenedProp) {
          setIsOpened(false);
          onAnimationEnd?.();
        }
      },
    },
    dialog: {
      className: 'modal__dialog',
      role: 'dialog',
      'aria-modal': true,
      onClick: (e: React.MouseEvent<HTMLDivElement>) => {
        e.stopPropagation();
      },
      ...(width ? { style: { width } } : {}),
    },
    dialogContent: {
      className: 'modal__dialog-content',
    },
    modalHeader: headerProps
      ? {
          ...headerProps,
          onClose,
        }
      : null,
    modalFooter: footerProps
      ? {
          ...footerProps,
          onCancel: onClose,
        }
      : null,
  };

  useEffect(() => {
    if (isOpenedProp) {
      setIsOpened(true);
    }
  }, [isOpenedProp]);

  if (!isOpened) {
    return null;
  }

  return createPortal(
    <div {...attrs.container}>
      <div {...attrs.dialog}>
        {attrs.modalHeader ? <ModalHeader {...attrs.modalHeader} /> : null}
        <div {...attrs.dialogContent}>{children}</div>
        {attrs.modalFooter ? <ModalFooter {...attrs.modalFooter} /> : null}
      </div>
    </div>,
    document.querySelector('#modal-root')!
  );
};

export default Modal;
